import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config/config';
import { decodeToken, isExpired } from "react-jwt";
import Header from '../others/Header';
import { useTour } from '../components/TourContext';
import { FaCompass } from 'react-icons/fa';
import CardLogoEmpresa from '../components/CardLogoEmpresa';

const SettingsPage = ({ user }) => {
    const { abrirTour } = useTour();
    const [loading, setLoading] = useState(false);
    const [loadingLinks, setLoadingLinks] = useState(false);
    const { API_BASE_URL } = config;
    const [message, setMessage] = useState({ text: '', type: '' });
    const [backupOptions, setBackupOptions] = useState({
        format: 'sql',
        includeMedia: false,
        backupName: `backup_${new Date().toISOString().split('T')[0]}`
    });

    // Estados para links úteis
    const [usefulLinks, setUsefulLinks] = useState([]);
    const [newLink, setNewLink] = useState({
        title: '',
        url: '',
        color: '#3498db'
    });
    const [userData, setUserData] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem("jwtToken");
        if (token === null || typeof token === "undefined" || isExpired(token)) {
            window.location.href = "/login";
        } else {
            const decoded = decodeToken(token);
            setUserData(decoded);
            console.log('TESTEEEE ', decoded)
        }
    }, []);


    // Uso do armazenamento (só empresas com acesso web têm plano e limite)
    const [armazenamento, setArmazenamento] = useState(null);

    // Carregar links ao montar o componente
    useEffect(() => {
        loadUsefulLinks();
        loadArmazenamento();
    }, []);

    const loadArmazenamento = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/empresas/minha/armazenamento`, getAuthHeader());
            setArmazenamento(response.data);
        } catch (error) {
            console.error('Erro ao carregar armazenamento:', error);
        }
    };

    const loadUsefulLinks = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/links`, getAuthHeader());
            setUsefulLinks(response.data);
        } catch (error) {
            console.error('Erro ao carregar links:', error);
        }
    };

    const handleLinkInputChange = (e) => {
        const { name, value } = e.target;
        setNewLink(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddLink = async () => {
        if (!newLink.title.trim() || !newLink.url.trim()) {
            setMessage({ text: 'Preencha todos os campos', type: 'error' });
            return;
        }

        setLoadingLinks(true);
        try {
            // Garantir que a cor tenha o formato correto
            const linkData = {
                ...newLink,
                color: newLink.color.startsWith('#') ? newLink.color : `#${newLink.color}`
            };

            const response = await axios.post(`${API_BASE_URL}/links`, linkData, getAuthHeader());
            setUsefulLinks(prev => [...prev, response.data]);
            setNewLink({ title: '', url: '', color: '#3498db' }); // Reset para cor padrão
            setMessage({ text: 'Link adicionado com sucesso!', type: 'success' });
        } catch (error) {
            console.error('Erro ao adicionar link:', error);
            setMessage({ text: 'Erro ao adicionar link', type: 'error' });
        } finally {
            setLoadingLinks(false);
        }
    };

    const handleDeleteLink = async (linkId) => {
        try {
            await axios.delete(`${API_BASE_URL}/links/${linkId}`, getAuthHeader());
            setUsefulLinks(prev => prev.filter(link => link.id !== linkId));
            setMessage({ text: 'Link removido com sucesso!', type: 'success' });
        } catch (error) {
            console.error('Erro ao remover link:', error);
            setMessage({ text: 'Erro ao remover link', type: 'error' });
        }
    };

    const getAuthHeader = () => {
        const token = localStorage.getItem('jwtToken');
        return {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        };
    };

    const handleDownloadBackup = async () => {
        setLoading(true);
        setMessage({ text: '', type: '' });

        try {
            const response = await axios.get(`${API_BASE_URL}/backup/download`, {
                responseType: 'blob',
                headers: getAuthHeader().headers
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'backup.zip');
            document.body.appendChild(link);
            link.click();
            link.remove();

        } catch (error) {
            console.error('Erro ao baixar backup:', error);
            // Com responseType 'blob' o corpo do erro chega como Blob: lê o JSON para mostrar a mensagem do servidor
            let errorMsg = 'Erro ao baixar backup. Por favor, tente novamente.';
            try {
                const corpo = error.response?.data instanceof Blob ? JSON.parse(await error.response.data.text()) : error.response?.data;
                if (corpo?.message) errorMsg = corpo.message;
            } catch (e) { /* mantém a mensagem padrão */ }
            setMessage({ text: errorMsg, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setBackupOptions(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    return (
        <div>
            <Header />
            <div style={styles.container}>
                <div style={styles.header}>
                    <h2 style={styles.title}>Configurações do Sistema</h2>
                    <p style={styles.subtitle}>Gerencie as configurações e preferências do sistema</p>
                </div>

                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Tour do Sistema</h3>
                    <p style={styles.cardDescription}>
                        Reveja a explicação guiada dos principais fluxos do sistema (clientes, veículos, serviços e ordens de serviço).
                    </p>
                    <button onClick={abrirTour} style={styles.button}>
                        <FaCompass style={{ marginRight: '8px' }} /> Ver tour do sistema novamente
                    </button>
                </div>

                {/* Logo da empresa na página inicial: só quem administra a empresa altera */}
                {(userData?.tipoUsuario === 'GERENTE' || userData?.tipoUsuario === 'MANAGER') && <CardLogoEmpresa />}

                {armazenamento?.tipoAcesso === 'WEB' && <CardArmazenamento dados={armazenamento} />}

                {(userData?.tipoUsuario === 'MANAGER' || (userData?.tipoUsuario === 'GERENTE' && userData?.possuiServerLocal === true)) ? (
                    <div style={styles.card}>
                        <h3 style={styles.cardTitle}>Backup de Dados</h3>
                        <p style={styles.cardDescription}>
                            Gere e faça download de backups completos do banco de dados.
                        </p>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Nome do Backup:
                                <input
                                    type="text"
                                    name="backupName"
                                    value={backupOptions.backupName}
                                    onChange={handleInputChange}
                                    style={styles.input}
                                    placeholder="Digite um nome para o backup"
                                />
                            </label>
                        </div>

                        {message.text && (
                            <div style={{
                                ...styles.alert,
                                ...(message.type === 'error' ? styles.alertError : styles.alertSuccess)
                            }}>
                                {message.text}
                            </div>
                        )}

                        <button
                            onClick={handleDownloadBackup}
                            disabled={loading}
                            style={loading ? { ...styles.button, ...styles.buttonLoading } : styles.button}
                        >
                            {loading ? (
                                <>
                                    <span style={styles.spinner}></span>
                                    Gerando Backup...
                                </>
                            ) : (
                                'Gerar Backup'
                            )}
                        </button>
                    </div>
                ) : ''}
                {/* Links Úteis do Dashboard: cadastro numa linha só e lista enxuta (não é uma configuração importante) */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Links Úteis do Dashboard</h3>
                    <p style={styles.cardDescription}>Atalhos que aparecem no menu da página inicial.</p>

                    <div style={styles.linkForm}>
                        <input
                            type="text"
                            name="title"
                            value={newLink.title}
                            onChange={handleLinkInputChange}
                            style={{ ...styles.linkCampo, flex: '1 1 160px' }}
                            placeholder="Título"
                            aria-label="Título do link"
                        />
                        <input
                            type="url"
                            name="url"
                            value={newLink.url}
                            onChange={handleLinkInputChange}
                            style={{ ...styles.linkCampo, flex: '2 1 220px' }}
                            placeholder="https://exemplo.com"
                            aria-label="Endereço do link"
                        />
                        <input
                            type="color"
                            name="color"
                            value={newLink.color}
                            onChange={handleLinkInputChange}
                            style={styles.linkCor}
                            title="Cor do link"
                            aria-label="Cor do link"
                        />
                        <button
                            onClick={handleAddLink}
                            disabled={loadingLinks || !newLink.title.trim() || !newLink.url.trim()}
                            style={
                                loadingLinks || !newLink.title.trim() || !newLink.url.trim()
                                    ? { ...styles.button, ...styles.buttonDisabled }
                                    : styles.button
                            }
                        >
                            {loadingLinks ? 'Adicionando...' : 'Adicionar'}
                        </button>
                    </div>

                    {usefulLinks.length > 0 && (
                        <ul style={styles.linksLista}>
                            {usefulLinks.map((link) => (
                                <li key={link.id} style={styles.linkLinha}>
                                    <span style={{ ...styles.linkBolinha, backgroundColor: link.color }} />
                                    <span style={styles.linkTitle}>{link.title}</span>
                                    <span style={styles.linkUrl}>{link.url}</span>
                                    <button onClick={() => handleDeleteLink(link.id)} style={styles.linkExcluir} title="Excluir link">
                                        Excluir
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

// Estilos atualizados e corrigidos
const formatarBytes = (bytes) => {
    const gb = 1024 * 1024 * 1024;
    if (bytes >= gb) return `${(bytes / gb).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} GB`;
    return `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
};

// Barra de uso do armazenamento contratado. Verde até 80%, laranja até 95%, vermelho acima.
const CardArmazenamento = ({ dados }) => {
    if (dados.limiteBytes == null) {
        return (
            <div style={styles.card}>
                <h3 style={styles.cardTitle}>Armazenamento</h3>
                <p style={styles.cardDescription}>
                    Nenhum plano de armazenamento definido para sua empresa. Arquivos enviados até agora:{' '}
                    <strong>{formatarBytes(dados.usadoBytes || 0)}</strong>.
                </p>
            </div>
        );
    }

    const percentual = dados.percentualUsado || 0;
    const cor = percentual >= 95 ? '#e53e3e' : percentual >= 80 ? '#dd6b20' : '#38a169';

    return (
        <div style={styles.card}>
            <h3 style={styles.cardTitle}>Armazenamento</h3>
            <p style={styles.cardDescription}>
                Plano <strong>{dados.plano}</strong> ({dados.capacidadeGb} GB). Documentos enviados (clientes e veículos) ocupam este espaço.
            </p>
            <div style={styles.barraFundo} role="progressbar" aria-valuenow={Math.round(percentual)} aria-valuemin={0} aria-valuemax={100}>
                <div style={{ ...styles.barraUso, width: `${Math.max(percentual, percentual > 0 ? 1 : 0)}%`, backgroundColor: cor }} />
            </div>
            <div style={styles.barraLegenda}>
                <span><strong>{formatarBytes(dados.usadoBytes)}</strong> usados ({percentual.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)</span>
                <span><strong>{formatarBytes(dados.disponivelBytes)}</strong> disponíveis de {formatarBytes(dados.limiteBytes)}</span>
            </div>
            {percentual >= 100 ? (
                <p style={{ ...styles.barraAviso, color: '#e53e3e' }}>
                    Espaço esgotado: novos arquivos não poderão ser enviados. Exclua arquivos que não precisa mais ou contrate um plano maior.
                </p>
            ) : percentual >= 80 && (
                <p style={{ ...styles.barraAviso, color: '#dd6b20' }}>
                    Seu espaço está acabando. Ao atingir o limite, novos arquivos não poderão ser enviados.
                </p>
            )}
        </div>
    );
};

const styles = {
    barraFundo: {
        width: '100%',
        height: '14px',
        backgroundColor: '#edf2f7',
        borderRadius: '7px',
        overflow: 'hidden',
    },
    barraUso: {
        height: '100%',
        borderRadius: '7px',
        transition: 'width 0.4s ease',
    },
    barraLegenda: {
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '0.5rem',
        marginTop: '0.5rem',
        fontSize: '0.875rem',
        color: '#4a5568',
    },
    barraAviso: {
        fontSize: '0.875rem',
        margin: '0.75rem 0 0 0',
    },
    container: {
        flex: 1,
        padding: '2rem',
        paddingLeft: '60px',
        backgroundColor: '#f5f7fa',
        minHeight: '100vh'
    },
    header: {
        marginBottom: '2rem',
    },
    title: {
        fontSize: '1.5rem',
        fontWeight: '600',
        color: '#2d3748',
        margin: '0 0 0.5rem 0',
    },
    subtitle: {
        fontSize: '0.875rem',
        color: '#718096',
        margin: '0',
    },
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '0.5rem',
        padding: '1.5rem',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        marginBottom: '1.5rem',
    },
    cardTitle: {
        fontSize: '1.125rem',
        fontWeight: '600',
        color: '#2d3748',
        margin: '0 0 0.5rem 0',
    },
    cardDescription: {
        fontSize: '0.875rem',
        color: '#718096',
        margin: '0 0 1rem 0',
    },
    formGroup: {
        marginBottom: '1rem',
    },
    label: {
        display: 'block',
        marginBottom: '0.5rem',
        fontSize: '0.875rem',
        fontWeight: '500',
        color: '#4a5568',
    },
    input: {
        width: '100%',
        padding: '0.5rem 0.75rem',
        fontSize: '0.875rem',
        border: '1px solid #e2e8f0',
        borderRadius: '0.375rem',
        backgroundColor: '#fff',
        transition: 'border-color 0.2s',
        outline: 'none',
        boxSizing: 'border-box',
    },
    button: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.5rem 1rem',
        backgroundColor: '#4299e1',
        color: '#fff',
        fontSize: '0.875rem',
        fontWeight: '500',
        borderRadius: '0.375rem',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        outline: 'none',
    },
    buttonLoading: {
        backgroundColor: '#bee3f8',
        cursor: 'not-allowed',
    },
    buttonDisabled: {
        backgroundColor: '#a0aec0',
        cursor: 'not-allowed',
    },
    spinner: {
        display: 'inline-block',
        width: '1rem',
        height: '1rem',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '50%',
        borderTopColor: '#fff',
        animation: 'spin 1s linear infinite',
        marginRight: '0.5rem',
    },
    alert: {
        padding: '0.75rem 1rem',
        borderRadius: '0.375rem',
        marginBottom: '1rem',
        fontSize: '0.875rem',
    },
    alertSuccess: {
        backgroundColor: '#f0fff4',
        color: '#2f855a',
        border: '1px solid #c6f6d5',
    },
    alertError: {
        backgroundColor: '#fff5f5',
        color: '#c53030',
        border: '1px solid #fed7d7',
    },

    // Links úteis: formulário numa linha e lista enxuta
    linkForm: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    linkCampo: {
        minWidth: 0,
        padding: '6px 10px',
        border: '1px solid #e2e8f0',
        borderRadius: '0.375rem',
        fontSize: '0.85rem',
    },
    linkCor: {
        width: '34px',
        height: '32px',
        padding: '2px',
        border: '1px solid #e2e8f0',
        borderRadius: '0.375rem',
        background: '#fff',
        cursor: 'pointer',
        flexShrink: 0,
    },
    linksLista: {
        listStyle: 'none',
        margin: '10px 0 0 0',
        padding: 0,
        borderTop: '1px solid #edf2f7',
    },
    linkLinha: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '5px 2px',
        borderBottom: '1px solid #edf2f7',
        fontSize: '0.85rem',
        minWidth: 0,
    },
    linkBolinha: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        flexShrink: 0,
    },
    linkTitle: {
        fontWeight: '600',
        color: '#2d3748',
        whiteSpace: 'nowrap',
    },
    linkUrl: {
        flex: 1,
        minWidth: 0,
        fontSize: '0.78rem',
        color: '#718096',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    linkExcluir: {
        padding: '2px 8px',
        background: 'none',
        color: '#c53030',
        border: '1px solid #fed7d7',
        borderRadius: '0.25rem',
        fontSize: '0.75rem',
        cursor: 'pointer',
        flexShrink: 0,
    },
};

// Adicione isso ao seu CSS global ou dentro do componente
const globalStyles = `
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

export default SettingsPage;